import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import NewTrialModal from './NewTrialModal';

// ─── Section tab definitions ──────────────────────────────────────────────────

const SECTIONS = [
  {
    key: 'identity',
    label: 'Identity',
    columns: [
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
    ],
  },
  {
    key: 'trial_info',
    label: 'Trial Info',
    columns: [
      { key: 'trialSource', label: 'Trial Source' },
      { key: 'startDate',   label: 'Start Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
      { key: 'trialDay',    label: 'Day #' },
    ],
  },
  {
    key: 'verification',
    label: 'Verification',
    columns: [
      { key: 'verificationStatus', label: 'ID Status',     render: v => <VerifyBadge status={v} /> },
      { key: 'verifyMethod',       label: 'Verify Method' },
    ],
  },
  {
    key: 'day1_setup',
    label: 'Day 1 Setup',
    columns: [
      { key: 'skillLevel',       label: 'Level' },
      { key: 'preferredTime1',   label: 'Pref. Time 1' },
      { key: 'preferredTime2',   label: 'Pref. Time 2' },
      { key: 'suggestedBy',      label: 'Suggested By' },
      { key: 'suggestionDept',   label: 'Suggestion Dept' },
      { key: 'suggestionsGiven', label: 'Suggestions Given' },
      { key: 'confirmBooked',    label: 'Confirm Booked?', render: v => v ? <span className="badge badge-green">Yes</span> : '—' },
      { key: 'bookedBy',         label: 'Booked By' },
      { key: 'programsBooked',   label: 'Programs Booked' },
    ],
  },
  {
    key: 'visit_checkpoints',
    label: 'Visit Checkpoints',
    columns: [
      { key: 'visits',        label: 'Total Visits' },
      { key: 'lastVisitDate', label: 'Last Visit', render: v => v ? new Date(v).toLocaleDateString() : '—' },
      { key: 'riskLevel',     label: 'At Risk?', render: v => v === 'at_risk' ? <span className="badge badge-red">Yes</span> : <span className="badge badge-green">No</span> },
    ],
  },
  {
    key: 'intervention',
    label: 'Intervention',
    columns: [
      { key: 'interventionMade', label: 'Made?', render: v => v ? <span className="badge badge-green">Yes</span> : '—' },
      { key: 'interventionType', label: 'Type' },
      { key: 'interventionBy',   label: 'By' },
      { key: 'interventionDate', label: 'Date' },
    ],
  },
  {
    key: 'manager',
    label: 'Manager',
    columns: [
      { key: 'managerConnection', label: 'Connected?', render: v => v ? <span className="badge badge-green">Yes</span> : <span className="badge badge-yellow">No</span> },
      { key: 'managerName',       label: 'Manager' },
      { key: 'connectionDate',    label: 'Connection Date', render: v => v ? new Date(v).toLocaleDateString() : '—' },
    ],
  },
  {
    key: 'close',
    label: 'Close & Outcome',
    columns: [
      { key: 'status',              label: 'Trial Status',  render: (v, t) => <StatusBadge status={v} riskLevel={t.riskLevel} /> },
      { key: 'nextAction',          label: 'Next Action' },
      { key: 'conversionOfferDate', label: 'Offer Date',    render: v => v ? new Date(v).toLocaleDateString() : '—' },
      { key: 'promoPresented',      label: 'Promo?',        render: v => v ? <span className="badge badge-green">Yes</span> : '—' },
      { key: 'finalOutcome',        label: 'Final Outcome' },
      { key: 'notes',               label: 'Notes' },
    ],
  },
];

const ALL_COLUMNS = SECTIONS.flatMap(s => s.columns);

// ─── Full 15-KPI bar ──────────────────────────────────────────────────────────
// overview uses camelCase keys as returned by the API (okResponse wraps JS objects)

function buildKPIs(overview, trials) {
  if (overview) {
    const active    = Number(overview.activeTrials   || 0);
    const converted = Number(overview.converted      || 0);
    const lost      = Number(overview.lost           || 0);
    const trialToMember = (active + converted + lost) > 0
      ? `${Math.round((converted / (active + converted + lost)) * 100)}%`
      : '—';
    const pct = v => v != null ? `${Math.round(v)}%` : '—';

    return [
      { label: 'Active Trials',       value: active },
      { label: 'Converted',           value: converted },
      { label: '% Converted',         value: pct(overview.conversionRate) },
      { label: '% 0 Visits',          value: pct(overview.pctZeroVisits) },
      { label: '% 2+ Visits',         value: pct(overview.pct2PlusVisits) },
      { label: '% 3+ Visits',         value: pct(overview.pct3PlusVisits) },
      { label: '% 4+ Visits',         value: pct(overview.pct4PlusVisits) },
      { label: 'Avg Visits',          value: overview.avgVisits           ?? '—' },
      { label: '% Suggestions',       value: pct(overview.pctSuggestions) },
      { label: '% Confirm Booked',    value: pct(overview.pctConfirmBooked) },
      { label: '% Promo Presented',   value: pct(overview.pctPromoPresented) },
      { label: '% Interventions',     value: pct(overview.pctInterventions) },
      { label: '% Mgr. Connection',   value: pct(overview.pctMgrConnection) },
      { label: 'Trial→Member %',      value: trialToMember },
      { label: 'Avg Days to Convert', value: overview.avgDaysToConvert    ?? '—' },
    ];
  }

  // Fallback: compute from local trial data while overview loads
  const total      = trials.length;
  const converted  = trials.filter(t => t.status === 'converted').length;
  const zeroVisits = trials.filter(t => t.visits === 0).length;
  const twoPlus    = trials.filter(t => t.visits >= 2).length;
  const threePlus  = trials.filter(t => t.visits >= 3).length;
  const fourPlus   = trials.filter(t => t.visits >= 4).length;
  const avgVisits  = total > 0
    ? (trials.reduce((s, t) => s + (t.visits || 0), 0) / total).toFixed(1)
    : '0.0';
  const pct = n => total > 0 ? `${Math.round((n / total) * 100)}%` : '—';

  return [
    { label: 'Active Trials',       value: total },
    { label: 'Converted',           value: converted },
    { label: '% Converted',         value: pct(converted) },
    { label: '% 0 Visits',          value: pct(zeroVisits) },
    { label: '% 2+ Visits',         value: pct(twoPlus) },
    { label: '% 3+ Visits',         value: pct(threePlus) },
    { label: '% 4+ Visits',         value: pct(fourPlus) },
    { label: 'Avg Visits',          value: avgVisits },
    { label: '% Suggestions',       value: '—' },
    { label: '% Confirm Booked',    value: '—' },
    { label: '% Promo Presented',   value: '—' },
    { label: '% Interventions',     value: '—' },
    { label: '% Mgr. Connection',   value: '—' },
    { label: 'Trial→Member %',      value: '—' },
    { label: 'Avg Days to Convert', value: '—' },
  ];
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function T2MRecords({ onSelectTrial }) {
  const { get }         = useApi();
  const { currentClub } = useClub();
  const [trials, setTrials]     = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [showNew, setShowNew]   = useState(false);
  const [activeSection, setActiveSection] = useState('all');

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      get(`/t2m/trials?club=${currentClub.id}`),
      get(`/t2m/overview?club=${currentClub.id}`),
    ])
      .then(([trialsData, overviewData]) => {
        setTrials(trialsData.trials || []);
        setOverview(overviewData || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClub.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreated = (trialId) => {
    setShowNew(false);
    loadData();
    if (trialId) onSelectTrial(trialId);
  };

  const filtered = trials.filter(t =>
    (t.name  || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.phone || '').includes(search) ||
    (t.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const kpis = buildKPIs(overview, filtered);

  const visibleColumns = activeSection === 'all'
    ? ALL_COLUMNS
    : (SECTIONS.find(s => s.key === activeSection)?.columns || ALL_COLUMNS);

  return (
    <div>
      {showNew && (
        <NewTrialModal
          onClose={() => setShowNew(false)}
          onCreated={handleCreated}
        />
      )}

      {/* 15-KPI horizontally scrollable bar */}
      <div style={styles.kpiScroll}>
        <div style={styles.kpiBar}>
          {kpis.map((k, i) => (
            <div key={k.label} style={{
              ...styles.kpiItem,
              ...(i === kpis.length - 1 ? { borderRight: 'none' } : {}),
            }}>
              <div style={styles.kpiValue}>{k.value}</div>
              <div style={styles.kpiLabel}>{k.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search + New Trial */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <input
          className="field-input"
          placeholder="Search by name, email, or phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          + New Trial
        </button>
      </div>

      {/* Section tabs */}
      <div style={styles.tabBar}>
        <button
          style={{ ...styles.tab, ...(activeSection === 'all' ? styles.tabActive : {}) }}
          onClick={() => setActiveSection('all')}
        >
          All Columns
        </button>
        {SECTIONS.map(s => (
          <button
            key={s.key}
            style={{ ...styles.tab, ...(activeSection === s.key ? styles.tabActive : {}) }}
            onClick={() => setActiveSection(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, ...styles.frozenCol }}>Name</th>
                {visibleColumns.map(col => (
                  <th key={col.key} style={styles.th}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={visibleColumns.length + 1}
                    style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}
                  >
                    No records found
                  </td>
                </tr>
              )}
              {filtered.map(t => (
                <tr
                  key={t.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onSelectTrial(t.id)}
                  className="table-row-hover"
                >
                  <td style={{ ...styles.td, ...styles.frozenCol, fontWeight: 500 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {t.riskLevel === 'at_risk' && (
                        <span style={{ color: 'var(--red)', fontSize: '0.7rem' }}>●</span>
                      )}
                      {t.name}
                    </div>
                  </td>
                  {visibleColumns.map(col => (
                    <td key={col.key} style={styles.td}>
                      {col.render
                        ? col.render(t[col.key], t)
                        : (t[col.key] !== undefined && t[col.key] !== null && t[col.key] !== '')
                          ? String(t[col.key])
                          : <span style={{ color: 'var(--text-secondary)' }}>—</span>
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
        {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        {search && ` matching "${search}"`}
      </div>
    </div>
  );
}

// ─── Badges ───────────────────────────────────────────────────────────────────

function StatusBadge({ status, riskLevel }) {
  if (riskLevel === 'at_risk') return <span className="badge badge-red">At Risk</span>;
  if (status === 'converted')  return <span className="badge badge-green">Converted</span>;
  if (status === 'lost')       return <span className="badge badge-gray">Lost</span>;
  return <span className="badge badge-blue">Active</span>;
}

function VerifyBadge({ status }) {
  if (status === 'verified') return <span className="badge badge-green">Verified</span>;
  if (status === 'pending')  return <span className="badge badge-yellow">Pending</span>;
  return <span className="badge badge-gray">—</span>;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  kpiScroll: {
    overflowX: 'auto',
    marginBottom: '1rem',
    borderRadius: '8px',
    border: '1px solid var(--border)',
  },
  kpiBar: {
    display: 'flex',
    background: 'var(--surface-2)',
    borderRadius: '8px',
    overflow: 'hidden',
    minWidth: 'max-content',
  },
  kpiItem: {
    padding: '0.6rem 0.9rem',
    borderRight: '1px solid var(--border)',
    minWidth: 100,
    textAlign: 'center',
    flexShrink: 0,
  },
  kpiValue: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: 'var(--text)',
    lineHeight: 1.2,
  },
  kpiLabel: {
    fontSize: '0.65rem',
    color: 'var(--text-secondary)',
    marginTop: '0.15rem',
    whiteSpace: 'nowrap',
  },
  tabBar: {
    display: 'flex',
    gap: '0.25rem',
    flexWrap: 'wrap',
    marginBottom: '0.6rem',
  },
  tab: {
    padding: '0.3rem 0.75rem',
    fontSize: '0.78rem',
    border: '1px solid var(--border)',
    borderRadius: '20px',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  tabActive: {
    background: 'var(--yellow)',
    color: '#000',
    borderColor: 'var(--yellow)',
    fontWeight: 600,
  },
  tableWrapper: {
    overflowX: 'auto',
    background: 'var(--surface-2)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    position: 'relative',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.83rem',
    minWidth: 600,
  },
  th: {
    padding: '0.55rem 0.85rem',
    textAlign: 'left',
    fontSize: '0.72rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid var(--border)',
    background: 'var(--surface-2)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.55rem 0.85rem',
    borderBottom: '1px solid var(--border)',
    color: 'var(--text)',
    whiteSpace: 'nowrap',
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  frozenCol: {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    background: 'var(--surface-2)',
    borderRight: '1px solid var(--border)',
    minWidth: 160,
  },
};
